import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import {
  FaTrashAlt,
  FaArrowLeft,
  FaShieldAlt,
  FaTruck,
  FaUndo,
} from "react-icons/fa";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import "./cart.css";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const pageVariants = {
  hidden: { opacity: 0, y: 50 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.97 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4 } },
  exit: { opacity: 0, x: -80, scale: 0.9, transition: { duration: 0.35 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 }
  }
};

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { pathname } = useLocation();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.exp * 1000 < Date.now()) {
          localStorage.removeItem('token');
        }
      } catch (e) {
        localStorage.removeItem('token');
      }
    }
  }, []);

  // Generate unique ID for cart item based on ALL variants
  const generateItemId = (item) => {
    return `${item.productId}_${item.color || "none"}_${item.band || "none"}_${item.size || "none"}_${item.storage || "none"}_${item.memory || "none"}_${item.processor || "none"}_${item.connectivity || "none"}`;
  };

  useEffect(() => {
    const loadCart = async () => {
      try {
        const guestId = localStorage.getItem("guestId");

        const res = await fetch(
          `${REACT_APP_API_URL}/cart?guestId=${guestId}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );

        const data = await res.json();

        const items = (data.items || []).map((i) => {
          // Create variant string for display
          const variantParts = [];
          if (i.color) variantParts.push(i.color);
          if (i.band) variantParts.push(i.band);
          if (i.size) variantParts.push(i.size);
          if (i.storage) variantParts.push(i.storage);
          if (i.memory) variantParts.push(i.memory);
          if (i.processor) variantParts.push(i.processor);
          if (i.connectivity) variantParts.push(i.connectivity);
          
          return {
            id: generateItemId(i),
            productId: i.productId,
            name: i.name,
            price: i.price,
            originalPrice: i.originalPrice || i.price,
            qty: i.quantity,
            image: i.image,
            color: i.color || "",
            band: i.band || "",
            memory: i.memory || "",
            storage: i.storage || "",
            size: i.size || "",
            connectivity: i.connectivity || "",
            processor: i.processor || "",
            sku: i.sku || "",
            variant: variantParts.join(" · "),
          };
        });
        
        console.log("Loaded cart items:", items);
        setCartItems(items);
      } catch (err) {
        console.error("Cart load failed", err);
      } finally {
        setLoading(false);
      }
    };

    loadCart();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  const updateQty = async (id, delta) => {
    const item = cartItems.find((i) => i.id === id);
    if (!item) return;

    if (item.qty === 1 && delta === -1) return;

    const newQty = item.qty + delta;

    // Update UI immediately
    setCartItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, qty: newQty } : i))
    );

    const guestId = localStorage.getItem("guestId");

    try {
      const response = await fetch(`${REACT_APP_API_URL}/cart/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          guestId,
          product: {
            productId: item.productId,
            name: item.name,
            price: item.price,
            quantity: newQty,
            image: item.image,
            color: item.color,
            band: item.band,
            memory: item.memory,
            storage: item.storage,
            size: item.size,
            connectivity: item.connectivity,
            processor: item.processor,
            sku: item.sku,
          },
        }),
      });

      if (!response.ok) {
        // Revert on error
        setCartItems((prev) =>
          prev.map((i) => (i.id === id ? { ...i, qty: item.qty } : i))
        );
        console.error("Failed to update cart");
      }
    } catch (err) {
      console.error("Update failed", err);
      setCartItems((prev) =>
        prev.map((i) => (i.id === id ? { ...i, qty: item.qty } : i))
      );
    }
  };

  const removeItem = async (item) => {
    // Remove from UI immediately
    setCartItems((prev) => prev.filter((i) => i.id !== item.id));

    const guestId = localStorage.getItem("guestId");

    try {
      const response = await fetch(
        `${REACT_APP_API_URL}/cart/remove/${item.productId}?guestId=${guestId}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            color: item.color,
            band: item.band,
            memory: item.memory,
            storage: item.storage,
            size: item.size,
            connectivity: item.connectivity,
            processor: item.processor,
          }),
        }
      );
      
      if (!response.ok) {
        console.error("Failed to remove item");
        // Reload cart to sync
        window.location.reload();
      }
    } catch (err) {
      console.error("Remove failed", err);
      // Reload cart to sync
      window.location.reload();
    }
  };

  const subtotal = cartItems.reduce((s, i) => s + i.price * i.qty, 0);
  const totalOriginal = cartItems.reduce(
    (s, i) => s + i.originalPrice * i.qty,
    0
  );
  const discount = totalOriginal - subtotal;
  const shipping = subtotal > 50000 ? 0 : 499;
  const total = subtotal + shipping;

  const fmt = (n) =>
    "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });

  if (loading) {
    return (
      <>
        <Header />
        <div className="cart-page text-center">
          <Container>Loading cart...</Container>
        </div>
        <Footer />
      </>
    );
  }

  if (cartItems.length === 0) {
    return (
      <>
        <Header />
        <div className="cart-page">
          <Container>
            <div className="empty-cart-wrap">
              <div className="empty-cart-icon">🛒</div>
              <h3>Your cart is empty</h3>
              <p>Discover amazing products in our store!</p>
              <Link to="/shop" className="shop-now-btn">
                Start Shopping
              </Link>
            </div>
          </Container>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />

      <motion.div
        className="cart-page"
        variants={pageVariants}
        initial="hidden"
        animate="show"
      >
        <Container>
          <Row className="mb-4 align-items-end">
            <Col>
              <motion.h2 className="cart-title" variants={fadeUp}>
                Shopping Cart
                <span className="cart-count">{cartItems.length} items</span>
              </motion.h2>
              <motion.p className="cart-subtitle" variants={fadeUp}>
                Review your items and proceed to checkout
              </motion.p>
            </Col>
            <Col xs="auto">
              <Link to="/shop" className="continue-shopping">
                <FaArrowLeft /> Continue Shopping
              </Link>
            </Col>
          </Row>

          <Row>
            <Col lg={8}>
              <AnimatePresence>
                {cartItems.map((item) => (
                  <motion.div
                    key={item.id}
                    className="cart-item-card"
                    variants={itemVariants}
                    exit="exit"
                    layout
                  >
                    <Row className="align-items-center">
                      <Col xs={3} sm={2}>
                        <div className="cart-item-img-wrap">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="cart-item-img"
                            onError={(e) => e.target.src = "/placeholder.jpg"}
                          />
                        </div>
                      </Col>

                      <Col xs={9} sm={4}>
                        <h6 className="cart-item-name">{item.name}</h6>
                        {item.variant && (
                          <p className="cart-item-variant">{item.variant}</p>
                        )}
                        <span className="cart-item-price">
                          {fmt(item.price)}
                        </span>
                      </Col>

                      <Col
                        xs={12}
                        sm={6}
                        className="d-flex align-items-center justify-content-sm-end gap-3 mt-3 mt-sm-0"
                      >
                        <div className="qty-stepper">
                          <button onClick={() => updateQty(item.id, -1)}>
                            −
                          </button>
                          <span>{item.qty}</span>
                          <button onClick={() => updateQty(item.id, 1)}>
                            +
                          </button>
                        </div>

                        <span className="item-subtotal">
                          {fmt(item.price * item.qty)}
                        </span>

                        <button
                          className="remove-btn"
                          onClick={() => removeItem(item)}
                        >
                          <FaTrashAlt />
                        </button>
                      </Col>
                    </Row>
                  </motion.div>
                ))}
              </AnimatePresence>
            </Col>

            <Col lg={4}>
              <div className="order-summary">
                <h5 className="summary-title">Order Summary</h5>
                <div className="summary-row">
                  <span>Subtotal</span>
                  <span>{fmt(totalOriginal)}</span>
                </div>
                {discount > 0 && (
                  <div className="summary-row">
                    <span>Discount</span>
                    <span className="discount">− {fmt(discount)}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span>Shipping</span>
                  <span>{shipping === 0 ? "FREE" : fmt(shipping)}</span>
                </div>

                <hr className="summary-divider" />

                <div className="summary-total">
                  <span>Total</span>
                  <span>{fmt(total)}</span>
                </div>

                <Link to="/checkout">
                  <button className="checkout-btn">
                    Proceed to Checkout
                  </button>
                </Link>

                <div className="trust-badges">
                  <span>
                    <FaShieldAlt /> Secure
                  </span>
                  <span>
                    <FaTruck /> Free Shipping
                  </span>
                  <span>
                    <FaUndo /> Easy Returns
                  </span>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </motion.div>

      <Footer />
    </>
  );
};

export default Cart;